import { useState } from 'react'
import { AlertCircle, Check, Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import type { CreateDrawLotteryRequest, DrawBetType, DrawBetTypeConfig, DrawScheduleConfig } from '@nexbit/types'
import {
  apiDrawRequestLotteryOtp, apiDrawCreateLottery, ApiRequestError,
} from '@/lib/api'
import {
  roundDraftsFrom, validateRounds, toRoundConfigs, describeRounds, MAX_ROUNDS, newRoundDraft,
  type RoundDraft,
} from '@/lib/schedule'
import { BET_TYPE_LABEL, BET_TYPE_ORDER, defaultBetConfig } from '@/lib/lottery-defaults'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/**
 * Add a lottery.
 *
 * Two steps on purpose. The first collects everything and asks the server for a code;
 * the second takes the code, which arrives in the operators' Telegram group rather than
 * here. That is what makes a stolen session insufficient — the action has to leave the
 * browser and be announced before it completes.
 *
 * The draft is frozen once a code is issued. The code is bound to a fingerprint of these
 * exact details, so editing them would only produce a mismatch at the server; freezing
 * says so up front instead of failing at the last step.
 */

const WEEKDAY_LABEL = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']

type Step = 'form' | 'confirm'

export function AddLotteryDialog({ onCreated, onCancel }: {
  onCreated: (name: string) => void
  onCancel: () => void
}) {
  const [step, setStep] = useState<Step>('form')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [icon, setIcon] = useState('')
  const [category, setCategory] = useState<'draw' | 'stock'>('draw')
  const [digitCount, setDigitCount] = useState('6')
  const [closeMins, setCloseMins] = useState('30')
  const [scheduleType, setScheduleType] = useState<DrawScheduleConfig['type']>('daily')
  const [rounds, setRounds] = useState<RoundDraft[]>(roundDraftsFrom(null))
  const [days, setDays] = useState<number[]>([])
  const [dates, setDates] = useState<number[]>([])
  const [betConfig, setBetConfig] = useState(() => defaultBetConfig('draw'))

  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [otp, setOtp] = useState('')

  const codeValid = /^[a-z][a-z0-9_]{2,29}$/.test(code)
  const minsValid = /^\d{1,4}$/.test(closeMins) && Number(closeMins) <= 1440
  // Up to 12, matching the column's own CHECK. 7 is a real case — หวย ธ.ก.ส. and ออมสิน
  // both announce seven digits — and a single-character regex silently made it unreachable.
  const digitsValid = /^(?:[2-9]|1[0-2])$/.test(digitCount)
  const roundsProblem = validateRounds(rounds)
  const anyBetType = BET_TYPE_ORDER.some((t) => betConfig[t].enabled)

  const canSubmitForm =
    codeValid && name.trim().length > 0 && minsValid && digitsValid && anyBetType
    && (scheduleType === 'manual' || (
      roundsProblem === null
      && (scheduleType !== 'weekly' || days.length > 0)
      && (scheduleType !== 'monthly' || dates.length > 0)
    ))

  /** Exactly what the server will be sent, and what the code is bound to. */
  function buildDraft(): CreateDrawLotteryRequest {
    const sorted = toRoundConfigs(rounds)
    const enabled = Object.fromEntries(
      BET_TYPE_ORDER.filter((t) => betConfig[t].enabled).map((t) => [t, betConfig[t]]),
    ) as Partial<Record<DrawBetType, DrawBetTypeConfig>>
    return {
      code: code.trim(),
      name: name.trim(),
      nameEn: nameEn.trim() || null,
      icon: icon.trim() || null,
      category,
      digitCount: Number(digitCount),
      betConfig: enabled,
      drawSchedule: scheduleType === 'manual'
        ? { type: 'manual', tzOffsetHours: 7 }
        : {
            type: scheduleType,
            rounds: sorted,
            drawTime: sorted[0]!.time,
            tzOffsetHours: 7,
            ...(scheduleType === 'weekly' ? { days } : {}),
            ...(scheduleType === 'monthly' ? { dates } : {}),
          },
      betCloseBeforeSeconds: Number(closeMins) * 60,
      isActive: false,
    }
  }

  async function requestCode() {
    setBusy(true); setErr(null)
    try {
      const issued = await apiDrawRequestLotteryOtp(buildDraft())
      setChallengeId(issued.challengeId)
      setExpiresAt(issued.expiresAt)
      setStep('confirm')
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'ขอรหัสยืนยันไม่สำเร็จ')
    } finally { setBusy(false) }
  }

  async function confirm() {
    if (!challengeId) return
    setBusy(true); setErr(null)
    try {
      const draft = buildDraft()
      await apiDrawCreateLottery(draft, { challengeId, code: otp })
      onCreated(draft.name)
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'เพิ่มหวยไม่สำเร็จ')
      setOtp('')
    } finally { setBusy(false) }
  }

  const chip = (active: boolean) =>
    cn(
      'h-7 min-w-9 rounded-[var(--radius-sm)] border px-2 font-mono text-xs transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground hover:bg-muted',
    )
  const toggle = (list: number[], v: number) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v].sort((a, b) => a - b)

  const patchBet = (t: DrawBetType, patch: Partial<DrawBetTypeConfig>) =>
    setBetConfig((prev) => ({ ...prev, [t]: { ...prev[t], ...patch } }))

  const num = (v: number, on: (n: number) => void, label: string) => (
    <Input
      inputMode="decimal"
      value={String(v)}
      aria-label={label}
      onChange={(e) => {
        const n = Number(e.target.value.replace(/[^\d.]/g, ''))
        if (Number.isFinite(n)) on(n)
      }}
      className="h-7 w-20 font-mono text-xs"
    />
  )

  return (
    <Dialog open onOpenChange={(v) => { if (!busy && !v) onCancel() }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            เพิ่มหวยใหม่
          </DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {step === 'form'
              ? 'หวยที่เพิ่มจะถูกปิดไว้ก่อน เปิดให้สมาชิกเห็นได้หลังตรวจความถูกต้องแล้ว'
              : 'รหัสยืนยันถูกส่งเข้ากลุ่มเทเลแกรมของระบบ กรอกรหัสเพื่อยืนยันการเพิ่มหวย'}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        {step === 'form' ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="รหัสหวย (code)" hint="a-z 0-9 _ · เปลี่ยนภายหลังไม่ได้" invalid={code !== '' && !codeValid}>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="stock_thai"
                  className="h-9 font-mono text-sm"
                />
              </Field>
              <Field label="ชื่อที่แสดง">
                <Input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))}
                  placeholder="หวยหุ้นไทย" className="h-9 text-sm" />
              </Field>
              <Field label="ชื่อภาษาอังกฤษ" hint="ว่างได้ — สมาชิกที่ใช้ EN จะเห็นชื่อไทยแทน">
                <Input value={nameEn} onChange={(e) => setNameEn(e.target.value.slice(0, 60))}
                  placeholder="Thai Stock Lotto" className="h-9 text-sm" />
              </Field>
              <Field label="ไอคอน (path หรือ URL)" hint="ว่างได้ — จะไม่มีรูปนำหน้าชื่อ">
                <Input value={icon} onChange={(e) => setIcon(e.target.value.slice(0, 300))}
                  placeholder="/lottery/ic-stock_thai.webp" className="h-9 font-mono text-xs" />
              </Field>
              <Field label="หมวดหมู่">
                <div className="flex gap-1.5">
                  {(['draw', 'stock'] as const).map((c) => (
                    <button key={c} type="button" className={chip(category === c)}
                      onClick={() => { setCategory(c); setBetConfig(defaultBetConfig(c)) }}>
                      {c === 'draw' ? 'หวยงวด' : 'หวยหุ้น'}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="จำนวนหลักที่ประกาศ" hint="2–12" invalid={!digitsValid}>
                <Input inputMode="numeric" value={digitCount}
                  onChange={(e) => setDigitCount(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  className="h-9 w-20 font-mono text-sm" />
              </Field>
              <Field label="ปิดรับก่อนออกผล (นาที)" hint="0–1440" invalid={!minsValid}>
                <Input inputMode="numeric" value={closeMins}
                  onChange={(e) => setCloseMins(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="h-9 w-24 font-mono text-sm" />
              </Field>
            </div>

            <div className="space-y-2 border-t border-border pt-3">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                ตารางออกผล
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {(['daily', 'weekly', 'monthly', 'manual'] as const).map((v) => (
                  <button key={v} type="button" className={chip(scheduleType === v)} onClick={() => setScheduleType(v)}>
                    {{ daily: 'ทุกวัน', weekly: 'รายสัปดาห์', monthly: 'รายเดือน', manual: 'กำหนดเอง' }[v]}
                  </button>
                ))}
              </div>

              {scheduleType === 'weekly' && (
                <div className="flex flex-wrap gap-1.5">
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    <button key={d} type="button" className={chip(days.includes(d))}
                      aria-pressed={days.includes(d)}
                      onClick={() => setDays((p) => toggle(p, d))}>{WEEKDAY_LABEL[d]}</button>
                  ))}
                </div>
              )}
              {scheduleType === 'monthly' && (
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <button key={d} type="button" className={cn(chip(dates.includes(d)), 'min-w-7 px-1')}
                      aria-pressed={dates.includes(d)}
                      onClick={() => setDates((p) => toggle(p, d))}>{d}</button>
                  ))}
                </div>
              )}

              {scheduleType !== 'manual' && (
                <div className="space-y-1.5">
                  {rounds.map((r, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Input type="time" value={r.time}
                        aria-label={`เวลาออกผล รอบที่ ${i + 1}`}
                        aria-invalid={roundsProblem?.index === i}
                        onChange={(e) => setRounds((p) => p.map((x, j) => j === i ? { ...x, time: e.target.value } : x))}
                        className="h-9 w-[7.5rem] shrink-0 font-mono text-sm" />
                      {rounds.length > 1 && (
                        <>
                          <Input value={r.labelTh} placeholder="ชื่อรอบ เช่น รอบเช้า"
                            aria-label={`ชื่อรอบที่ ${i + 1}`}
                            aria-invalid={roundsProblem?.kind === 'label' && roundsProblem.index === i}
                            onChange={(e) => setRounds((p) => p.map((x, j) => j === i ? { ...x, labelTh: e.target.value.slice(0, 24) } : x))}
                            className="h-9 min-w-0 flex-1 text-sm" />
                          <Button type="button" variant="ghost" size="icon"
                            aria-label={`ลบรอบที่ ${i + 1}`}
                            onClick={() => setRounds((p) => p.filter((_, j) => j !== i))}
                            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm"
                    disabled={rounds.length >= MAX_ROUNDS}
                    onClick={() => setRounds((p) => [...p, newRoundDraft()])}
                    className="h-7 gap-1 font-mono text-[11px]">
                    <Plus className="h-3 w-3" /> เพิ่มรอบ
                  </Button>
                  <p className={cn('font-mono text-[10px]', roundsProblem ? 'text-destructive' : 'text-muted-foreground')}>
                    {roundsProblem?.kind === 'duplicate' ? 'เวลาซ้ำกับรอบอื่น'
                      : roundsProblem?.kind === 'label' ? 'หวยหลายรอบต้องตั้งชื่อรอบให้ครบ'
                      : roundsProblem?.kind === 'time' ? 'เวลาไม่ถูกต้อง'
                      : `จะบันทึกเป็น: ${describeRounds(toRoundConfigs(rounds))}`}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 border-t border-border pt-3">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                ประเภทที่รับแทง และอัตราจ่าย
              </Label>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="pb-1 pr-2">รับแทง</th>
                      <th className="pb-1 pr-2">จ่าย</th>
                      <th className="pb-1 pr-2">ต่อไม้</th>
                      <th className="pb-1 pr-2">ต่อเลข</th>
                      <th className="pb-1">ต่อประเภท</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BET_TYPE_ORDER.map((t) => {
                      const c = betConfig[t]
                      return (
                        <tr key={t} className={cn('align-middle', !c.enabled && 'opacity-45')}>
                          <td className="py-0.5 pr-2">
                            <label className="flex cursor-pointer items-center gap-1.5">
                              <input type="checkbox" checked={c.enabled}
                                onChange={(e) => patchBet(t, { enabled: e.target.checked })}
                                className="h-3.5 w-3.5 accent-[var(--primary)]" />
                              <span className="whitespace-nowrap text-xs">{BET_TYPE_LABEL[t]}</span>
                            </label>
                          </td>
                          <td className="py-0.5 pr-2">{num(c.payout, (n) => patchBet(t, { payout: n }), `อัตราจ่าย ${BET_TYPE_LABEL[t]}`)}</td>
                          <td className="py-0.5 pr-2">{num(c.maxBet, (n) => patchBet(t, { maxBet: n }), `สูงสุดต่อไม้ ${BET_TYPE_LABEL[t]}`)}</td>
                          <td className="py-0.5 pr-2">{num(c.maxPerNumber, (n) => patchBet(t, { maxPerNumber: n }), `สูงสุดต่อเลข ${BET_TYPE_LABEL[t]}`)}</td>
                          <td className="py-0.5">{num(c.maxPerType, (n) => patchBet(t, { maxPerType: n }), `สูงสุดต่อประเภท ${BET_TYPE_LABEL[t]}`)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {!anyBetType && (
                <p className="font-mono text-[10px] text-destructive">ต้องเปิดรับแทงอย่างน้อยหนึ่งประเภท</p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-border bg-muted/40 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="space-y-1 font-mono text-xs">
                <p>รหัสยืนยัน 6 หลักถูกส่งเข้ากลุ่มเทเลแกรมของระบบแล้ว</p>
                <p className="text-muted-foreground">
                  ใช้ได้ครั้งเดียว · หมดอายุ {expiresAt ? new Date(expiresAt).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '—'} น.
                </p>
              </div>
            </div>

            <div className="rounded-[var(--radius-sm)] border border-border bg-card p-3 font-mono text-xs">
              <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">กำลังจะเพิ่ม</p>
              <p className="font-semibold">{name} <span className="text-muted-foreground">({code})</span></p>
              <p className="text-muted-foreground">
                {category === 'stock' ? 'หวยหุ้น' : 'หวยงวด'} · {digitCount} หลัก ·
                {scheduleType === 'manual' ? ' กำหนดเอง' : ` ${describeRounds(toRoundConfigs(rounds))}`} ·
                ปิดรับก่อน {closeMins} นาที
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground">
                แก้ข้อมูลหลังขอรหัสไม่ได้ — รหัสผูกกับข้อมูลชุดนี้ ถ้าจะแก้ต้องเริ่มใหม่
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="otp" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                รหัสยืนยัน
              </Label>
              <Input
                id="otp" inputMode="numeric" autoFocus autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="h-11 w-40 text-center font-mono text-lg tracking-[0.4em]"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" disabled={busy}
            onClick={step === 'confirm' ? () => { setStep('form'); setErr(null) } : onCancel}
            className="h-8 font-mono text-xs">
            {step === 'confirm' ? 'ย้อนกลับ' : 'ยกเลิก'}
          </Button>
          {step === 'form' ? (
            <Button size="sm" disabled={!canSubmitForm || busy}
              onClick={() => void requestCode()} className="h-8 gap-1.5 font-mono text-xs">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
              {busy ? 'กำลังส่งรหัส...' : 'ขอรหัสยืนยัน'}
            </Button>
          ) : (
            <Button size="sm" disabled={otp.length !== 6 || busy}
              onClick={() => void confirm()} className="h-8 gap-1.5 font-mono text-xs">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {busy ? 'กำลังเพิ่ม...' : 'ยืนยันเพิ่มหวย'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, hint, invalid, children }: {
  label: string; hint?: string; invalid?: boolean; children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
      {hint && (
        <p className={cn('font-mono text-[10px]', invalid ? 'text-destructive' : 'text-muted-foreground')}>{hint}</p>
      )}
    </div>
  )
}
